import { ActionCreatorWithoutPayload, ActionCreatorWithPayload, AsyncThunk, AsyncThunkConfig, EntityId } from "@reduxjs/toolkit";
import { AsyncEntityState } from "./asyncEntityStateUtil";
import { RootState, StoreAppDispatch } from "..";

export interface Filter {
  key: string,
  ids: number[]
}

function shouldSkipFetch<Id extends EntityId>(
  id: Id,
  lastFetchedById: Record<Id, number>,
  staleTime: number,
  overwriteLastFetch: boolean,
  loadingIds: Id[],
) {
  return (lastFetchedById[id] && ((Date.now() - lastFetchedById[id]) < staleTime && !overwriteLastFetch)) || loadingIds.includes(id)
}

export class EnqueueFetcher<T, Id extends EntityId> {
  protected flushTimeout: ReturnType<typeof setTimeout> | null = null;
  private fetchByIds: AsyncThunk<T[], Id[], AsyncThunkConfig>
  protected selectAsyncEntityState: (root: RootState) => AsyncEntityState<T, Id>;
  private clearPendingAction: ActionCreatorWithoutPayload;
  private addPendingId: ActionCreatorWithPayload<Id>;
  protected setLoadingIds: ActionCreatorWithPayload<Id[]>;
  protected staleTime = 5 * 60 * 1000; // 5 minutes


  constructor(fetchById: AsyncThunk<T[], Id[], AsyncThunkConfig>, selectAsyncEntityState: (root: RootState) => AsyncEntityState<T, Id>, clearPendingAction: ActionCreatorWithoutPayload, addPendingId: ActionCreatorWithPayload<Id>, setLoadingIds: ActionCreatorWithPayload<Id[]>) {
    this.fetchByIds = fetchById;
    this.selectAsyncEntityState = selectAsyncEntityState;
    this.clearPendingAction = clearPendingAction;
    this.addPendingId = addPendingId;
    this.setLoadingIds = setLoadingIds;
  }

  private flushPendingIds(dispatch: StoreAppDispatch, state: RootState) {
    const { enqueueIds } = this.selectAsyncEntityState(state);

    if (enqueueIds.length === 0) {
      return;
    }

    dispatch(this.clearPendingAction())

    dispatch(this.setLoadingIds(enqueueIds))

    dispatch(this.fetchByIds(enqueueIds))
  }

  enqueueFetchById = (id: Id, overwriteLastFetch: boolean = false) => (dispatch: StoreAppDispatch, getState: () => RootState) => {
    const { enqueueIds, loadingIds, lastFetchedById } = this.selectAsyncEntityState(getState());

    if (shouldSkipFetch(id, lastFetchedById, this.staleTime, overwriteLastFetch, loadingIds)) {
      return
    }

    if (!enqueueIds.includes(id)) {
      dispatch(this.addPendingId(id))
    }
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    this.flushTimeout = setTimeout(() => {
      this.flushPendingIds(dispatch, getState());
    }, 10);
  }

  enqueueFetchByIds = (ids: Id[], overwriteLastFetch: boolean = false) => (dispatch: StoreAppDispatch, getState: () => RootState) => {
    const { enqueueIds, loadingIds, lastFetchedById } = this.selectAsyncEntityState(getState());

    for (const id of ids) {
      if (shouldSkipFetch(id, lastFetchedById, this.staleTime, overwriteLastFetch, loadingIds)) {
        continue
      }

      if (!enqueueIds.includes(id)) {
        dispatch(this.addPendingId(id))
      }
      if (this.flushTimeout) {
        clearTimeout(this.flushTimeout);
      }
      this.flushTimeout = setTimeout(() => {
        this.flushPendingIds(dispatch, getState());
      }, 10);
    }
  }
}

// Enqueue fetcher with filter.

export class EnqueueFetcherWithFilterFetch<T> extends EnqueueFetcher<T, string> {

  constructor(fetchById: AsyncThunk<T[], string[], AsyncThunkConfig>, private fetchByFiltersThunk: AsyncThunk<T, Filter[], AsyncThunkConfig>, selectAsyncEntityState: (root: RootState) => AsyncEntityState<T, string>, clearPendingAction: ActionCreatorWithoutPayload, addPendingId: ActionCreatorWithPayload<string>, setLoadingIds: ActionCreatorWithPayload<string[]>) {
    super(fetchById, selectAsyncEntityState, clearPendingAction, addPendingId, setLoadingIds)
  }

  static convertFilterToId(filters: Filter[]): string {
    return filters.map(({ key, ids }) => `${key}=${ids.join(",")}`).join("&");
  }

  fetchByFilters = (filters: Filter[], overwriteLastFetch: boolean = false) => (dispatch: StoreAppDispatch, getState: () => RootState) => {
    const { loadingIds, lastFetchedById } = this.selectAsyncEntityState(getState());

    const id = EnqueueFetcherWithFilterFetch.convertFilterToId(filters)

    if (shouldSkipFetch(id, lastFetchedById, this.staleTime, overwriteLastFetch, loadingIds)) {
      return
    }

    dispatch(this.setLoadingIds([id]))

    dispatch(this.fetchByFiltersThunk(filters));
  }
}
